/**
 * Окно «Из готового кадра» панели «Новый видео-ролик» на `/projects` (задачи Оли №304/№305): готовые
 * картинки «Результатов» плиткой — ролик из генерации, не заходя в её карточку и без окна параметров.
 *
 * Лента — та же ручка, что у `/results` (`loadResults`, фильтр `status=done`), страницы — «Показать
 * ещё» по курсору `next_before`. Отбор — `videoFrames` (`lib/frameVideo.ts`), то есть тот же гейт,
 * что держит сервер: клип, видео и незавершённая генерация опорным кадром ролика не годятся.
 *
 * Кнопки «показывать» (№340): по умолчанию отмеченные и загруженные — именно из них делают ролик;
 * «остальные» включаются кнопкой. Отбор идёт на клиенте поверх уже загруженных страниц ленты.
 *
 * Выбор ОДИНОЧНЫЙ — проект растёт из одного кадра: клик по плитке отдаёт её вариант наверх и
 * закрывает окно (как `DressPickerDialog` в режиме `single`), проект создаёт страница.
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { RetryImg } from "@/components/trends/PinGrid";
import { Dialog } from "@/components/trends/Dialog";
import { btn, Empty, ErrorLine, Loading } from "@/components/trends/ui";
import { tileCaption, tileTitle } from "@/lib/editRuns";
import {
  FRAME_PICKER_DEFAULT,
  framePickerEmpty,
  framePickerFilter,
  videoFrames,
  type FramePickerFlags,
} from "@/lib/frameVideo";
import { thumbUrl, when } from "@/lib/media";
import { loadResults, resultKeys, type ResultFilter } from "@/lib/results";

/** Готовые генерации: незавершённые и упавшие гейт всё равно отсечёт — незачем их и возить. */
const DONE: ResultFilter = { status: "done" };

/** Кнопки отбора окна: подписи те же, что сотрудник видит в «Результатах». */
const FILTERS: { key: keyof FramePickerFlags; label: string }[] = [
  { key: "marked", label: "отмеченные" },
  { key: "uploaded", label: "загруженные" },
  { key: "rest", label: "остальные" },
];

export function FramePickerDialog({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  /** Выбран опорный кадр ролика — `variant.id` готовой картинки. */
  onPick: (variantId: number) => void;
}) {
  const feed = useInfiniteQuery({
    queryKey: resultKeys.feed(DONE),
    queryFn: ({ pageParam }) => loadResults(DONE, pageParam),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (p) => p.next_before ?? undefined,
    staleTime: 5_000,
    enabled: open,  // закрытое окно ленту не запрашивает
  });
  const pages = feed.data?.pages ?? [];
  // Фильтры окна (задача Оли №340): по умолчанию — отмеченные и загруженные, остальное по кнопке.
  // Отбор поверх `videoFrames`: гейт «это кадр, а не видео» кнопками не снимается.
  const [flags, setFlags] = useState<FramePickerFlags>(FRAME_PICKER_DEFAULT);
  const frames = framePickerFilter(videoFrames(pages.flatMap((p) => p.gens)), flags);
  const toggle = (key: keyof FramePickerFlags) => setFlags((f) => ({ ...f, [key]: !f[key] }));

  return (
    <Dialog open={open} onClose={onClose} title="Видео из готового кадра: выберите картинку" wide>
      <div className="flex flex-col gap-3 p-4" data-annot="выбор кадра для ролика">
        <div className="flex flex-wrap items-center gap-1.5 text-[12.5px]">
          <span className="text-fg-faint">показывать:</span>
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => toggle(key)}
              aria-pressed={flags[key]}
              className={
                "rounded-full border px-2.5 py-0.5 transition-colors " +
                (flags[key]
                  ? "border-accent bg-accent-bg text-fg"
                  : "border-border text-fg-muted hover:border-border-strong hover:text-fg")
              }
            >
              {label}
            </button>
          ))}
        </div>
        {feed.isPending ? <Loading /> : null}
        {feed.isError ? <ErrorLine error={feed.error} /> : null}
        {feed.data && framePickerEmpty(frames.length, feed.hasNextPage) ? (
          <Empty>
            {flags.rest && flags.marked && flags.uploaded
              ? "Готовых картинок нет — сгенерируйте или загрузите фото в «Результатах»."
              : "Под выбранные кнопки ничего не нашлось — включите «остальные»."}
          </Empty>
        ) : null}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
          {frames.map((g) => (
            <button
              key={g.variant_id}
              type="button"
              title={tileTitle(g)}
              onClick={() => onPick(g.variant_id)}
              className="flex flex-col gap-1 rounded-[var(--radius-card)] border border-border bg-bg p-1.5 text-left transition-colors hover:border-accent"
            >
              <div className="relative aspect-[3/4] w-full overflow-hidden rounded-[var(--radius-ui)] bg-bg-subtle">
                <RetryImg src={thumbUrl(g.asset_path)} alt="" className="size-full object-cover" />
              </div>
              <span className="truncate text-[12px] text-fg">{tileCaption(g)}</span>
              <span className="truncate text-[11px] text-fg-faint tnum">{when(g.date_create)}</span>
            </button>
          ))}
        </div>
        {feed.hasNextPage ? (
          <div>
            <button type="button" className={btn} disabled={feed.isFetchingNextPage} onClick={() => feed.fetchNextPage()}>
              {feed.isFetchingNextPage ? "Загружаю…" : "Показать ещё"}
            </button>
          </div>
        ) : null}
      </div>
      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-end gap-2 border-t border-border bg-bg px-4 py-2.5">
        <span className="mr-auto text-[12px] text-fg-faint">
          клик по плитке выбирает кадр ·{" "}
          <Link to="/results" className="text-accent-text hover:underline">
            все результаты ↗
          </Link>
        </span>
        <button type="button" className={btn} onClick={onClose}>
          Отмена
        </button>
      </div>
    </Dialog>
  );
}
